import { useCallback, useEffect, useRef } from 'react';

import useNoRenderRef from './use-no-render-ref';

/**
 * Successfully run a useEffect function once. Use the callback function passed to indicate when
 * you would no longer like the function to be run.
 * @param effect The effect to be run
 * @param deps The effects dependencies
 * @returns The unlock function, invoke this to allow the effect to be run again
 */
export default function useOnce(
  // biome-ignore lint/suspicious/noConfusingVoidType: historic
  effect: (preventSecondRun: () => void) => Promise<void | (() => void)> | (void | (() => void)),

  deps: React.DependencyList,
): () => void {
  const hasRunRef = useRef(false);
  const effectRef = useNoRenderRef(effect);

  function preventSecondRun() {
    hasRunRef.current = true;
  }

  const unlock = useCallback(() => {
    hasRunRef.current = false;
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    if (hasRunRef.current) return;

    effectRef.current(preventSecondRun);
  }, [...deps, effectRef]); // eslint-disable-line react-hooks/exhaustive-deps

  return unlock;
}
